import { createContext, useContext, type MutableRefObject } from 'react';

export type ProfileInlineEditSection =
    | null
    | 'company'
    | 'personal'
    | 'login'
    | 'contractor_company'
    | 'contractor_services'
    | 'contractor_availability'
    | 'contractor_terms';

/** ProfileSummary updates this each render; ProfileForm reads it for cross-area unsaved flows. */
export type ProfilePhotoInlineController = {
    isPhotoEditActive: boolean;
    /** Enter photo-edit chrome; optional `file` opens crop modal after. */
    completeOpenPhoto: (file?: File) => void;
    cancelPhotoEdit: () => void;
    /** After full profile save that included the new photo — exit chrome without reverting. */
    endPhotoEditAfterSuccessfulSubmit: () => void;
    /** Mobile: open native photo picker instead of showing browse UI. */
    openNativePhotoPicker?: () => void;
};

const defaultPhotoController: ProfilePhotoInlineController = {
    isPhotoEditActive: false,
    completeOpenPhoto: () => {},
    cancelPhotoEdit: () => {},
    endPhotoEditAfterSuccessfulSubmit: () => {},
};

export const defaultProfilePhotoInlineControllerRef: MutableRefObject<ProfilePhotoInlineController> =
    { current: defaultPhotoController };

const defaultInlineEditSnapshotRef: MutableRefObject<Record<
    string,
    unknown
> | null> = { current: null };

export type ProfileInlineUxValue = {
    inlineEditSection: ProfileInlineEditSection;
    beginInlineEdit: (section: Exclude<ProfileInlineEditSection, null>) => void;
    cancelInlineEdit: () => void;
    saveInlineEdit: () => void | Promise<void>;
    /** Form values captured when the current inline section was opened (used for save guards). */
    inlineEditSnapshotRef: MutableRefObject<Record<string, unknown> | null>;
    /** Skip company API → form resets while any profile inline section is open (shared form) */
    suspendCompanyResets: boolean;
    /** Skip profile API → form resets while any profile inline section is open */
    suspendProfileResets: boolean;
    /** Skip contractor query → form reset while any profile inline section is open */
    suspendContractorResets: boolean;
    profilePhotoInlineControllerRef: MutableRefObject<ProfilePhotoInlineController>;
    /** Camera: prompts if a card section has unsaved edits, then opens photo edit. */
    tryBeginPhotoEdit: () => void;
    /** Drop zone: same as camera; optional file opens the crop modal after. */
    requestUnsavedOpenPhoto: (file?: File) => void;
};

const noop = () => {};

const defaultValue: ProfileInlineUxValue = {
    inlineEditSection: null,
    beginInlineEdit: noop,
    cancelInlineEdit: noop,
    saveInlineEdit: noop,
    inlineEditSnapshotRef: defaultInlineEditSnapshotRef,
    suspendCompanyResets: false,
    suspendProfileResets: false,
    suspendContractorResets: false,
    profilePhotoInlineControllerRef: defaultProfilePhotoInlineControllerRef,
    tryBeginPhotoEdit: noop,
    requestUnsavedOpenPhoto: noop,
};

export const ProfileInlineUxContext =
    createContext<ProfileInlineUxValue>(defaultValue);

export function useProfileInlineUx() {
    return useContext(ProfileInlineUxContext);
}
